import {
  fetchText
} from "./chunk-CXYZ37JF.js";
import {
  toError
} from "./chunk-2GHSQBQ5.js";

// src/utils/decode-error.ts
var DecodeError = class _DecodeError extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
    Object.setPrototypeOf(this, _DecodeError.prototype);
    this.name = "DecodeError";
  }
};

// src/utils/network-error.ts
var NetworkError = class _NetworkError extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
    Object.setPrototypeOf(this, _NetworkError.prototype);
    this.name = "NetworkError";
  }
};

// src/utils/hls-media-playlist.ts
import { Parser } from "m3u8-parser";
async function loadHlsMediaPlaylist(url, options) {
  let data;
  try {
    data = await fetchText(url, options);
  } catch (e) {
    const error = toError(e);
    if (error.name === "AbortError") {
      throw error;
    }
    throw new NetworkError(`Failed to fetch media playlist: ${url}`, error);
  }
  return parseHlsMediaPlaylist(data, url);
}
function parseHlsMediaPlaylist(data, baseUrl) {
  const parser = new Parser();
  try {
    parser.push(data);
    parser.end();
  } catch (e) {
    throw new DecodeError("Failed to parse media playlist.", toError(e));
  }
  const manifest = parser.manifest;
  const fixedEnd = manifest.endList === true || manifest.playlistType === "VOD";
  const fixedStart = manifest.playlistType === "VOD" || manifest.playlistType === "EVENT";
  const programDateTimeMs = manifest.segments[0]?.programDateTime;
  const programDateTime = programDateTimeMs ? new Date(programDateTimeMs) : void 0;
  let playlistDuration = 0;
  const segments = [];
  const sequenceStart = manifest.mediaSequence ?? 0;
  for (let i = 0; i < manifest.segments.length; ++i) {
    const segment = manifest.segments[i];
    playlistDuration += segment.duration;
    segments.push({
      sequence: sequenceStart + i,
      duration: segment.duration,
      url: new URL(segment.uri, baseUrl).href,
      programDateTime: segment.programDateTime
    });
  }
  const averageSegmentDuration = manifest.segments.length === 0 ? manifest.targetDuration : Math.round(playlistDuration / manifest.segments.length);
  if (averageSegmentDuration === void 0) {
    throw new DecodeError("Media playlist is missing required tag EXT-X-TARGETDURATION.");
  }
  return {
    averageSegmentDuration,
    fixedEnd,
    fixedStart,
    playlistDuration,
    programDateTime,
    segments
  };
}

// src/utils/client-language.ts
function getClientLanguages() {
  return navigator.languages ? navigator.languages : navigator.language ? [navigator.language] : [];
}
function getMatchLanguages(tags) {
  const uniqueTags = new Set(tags);
  tags.forEach((tag) => uniqueTags.add(getPrimarySubtag(tag)));
  const resultTags = Array.from(uniqueTags);
  if (!uniqueTags.has("no")) {
    const firstMatch = resultTags.findIndex((tag) => /^(nb|nn|se)$/.exec(tag));
    if (firstMatch >= 0) {
      resultTags.splice(firstMatch + 1, 0, "no");
    }
  }
  return resultTags;
}
function orderByLanguage(items, getItemLanguage, preferredLanguages) {
  const afterPreferredIndex = preferredLanguages.length;
  const preferredIndexMap = items.map((item, itemIndex) => {
    const language = getItemLanguage(item);
    const fullMatchIndex = preferredLanguages.indexOf(language);
    if (fullMatchIndex !== -1) {
      return [fullMatchIndex, itemIndex];
    }
    const primarySubtagIndex = preferredLanguages.indexOf(getPrimarySubtag(language));
    if (primarySubtagIndex !== -1) {
      return [primarySubtagIndex, itemIndex];
    }
    return [afterPreferredIndex, itemIndex];
  });
  preferredIndexMap.sort((a, b) => a[0] - b[0]);
  return preferredIndexMap.map((indexes) => items[indexes[1]]);
}
function getPrimarySubtag(tag) {
  return tag.replace(/^((?:\w-)?\w+).*$/, "$1");
}

export {
  getClientLanguages,
  getMatchLanguages,
  orderByLanguage,
  DecodeError,
  NetworkError,
  loadHlsMediaPlaylist
};
